import type { CmmdComment } from "@cmmd-forge/contracts";
import { Bot, Pencil, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Avatar } from "../ui/avatar";
import { RowActionIconButton } from "../ui/row-action-icon-button";

interface TaskDetailCommentRowProps {
  readonly comment: CmmdComment;
  readonly editing: boolean;
  readonly deleting: boolean;
  readonly draft: string;
  readonly onBeginEdit: () => void;
  readonly onDraftChange: (value: string) => void;
  readonly onSave: () => void;
  readonly onCancel: () => void;
  readonly onDelete: () => void;
}

export function TaskDetailCommentRow(props: TaskDetailCommentRowProps) {
  const author = resolveCommentAuthor(props.comment);
  const canManage = Boolean(
    props.comment.commentKey &&
    (props.comment.permissions?.canEdit || props.comment.permissions?.canDelete),
  );
  return (
    <div className="group flex items-start gap-2.5">
      {props.comment.actor?.kind === "agent_member" ? (
        <span
          className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
          aria-label="Agent Member bot"
        >
          <Bot className="size-3" aria-hidden />
        </span>
      ) : (
        <Avatar name={author.name} src={author.image} className="mt-0.5 size-5" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <p className="text-xs font-medium text-foreground">{author.name}</p>
            {props.comment.actor?.kind === "agent_member" ? (
              <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-primary">
                Bot
              </span>
            ) : null}
          </div>
          {canManage && !props.editing ? <TaskDetailCommentActions {...props} /> : null}
        </div>
        {props.editing ? (
          <div className="mt-1 flex flex-col gap-1">
            <textarea
              autoFocus
              maxLength={10_000}
              value={props.draft}
              onChange={(event) => props.onDraftChange(event.currentTarget.value)}
              rows={2}
              className="w-full resize-none rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground outline-none"
            />
            <div className="flex gap-1.5">
              <Button
                size="sm"
                className="h-6 text-[11px]"
                disabled={!props.draft.trim()}
                onClick={props.onSave}
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-[11px]"
                onClick={props.onCancel}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-muted-foreground/80">
            {props.comment.content}
          </p>
        )}
        <p className="mt-0.5 tabular-nums text-[11px] text-muted-foreground/50">
          {props.comment.createdAt.slice(0, 16).replace("T", " ")}
        </p>
      </div>
    </div>
  );
}

function TaskDetailCommentActions(props: TaskDetailCommentRowProps) {
  return (
    <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
      {props.comment.permissions?.canEdit ? (
        <RowActionIconButton
          label="Edit comment"
          icon={<Pencil className="size-3" aria-hidden="true" />}
          onClick={props.onBeginEdit}
          variant="ghost"
        />
      ) : null}
      {props.comment.permissions?.canDelete ? (
        <RowActionIconButton
          label="Delete comment"
          icon={<Trash2 className="size-3" aria-hidden="true" />}
          disabled={props.deleting}
          onClick={props.onDelete}
          variant="ghost"
        />
      ) : null}
    </div>
  );
}

function resolveCommentAuthor(comment: CmmdComment): {
  readonly name: string;
  readonly image: string | null;
} {
  if (comment.actor?.kind === "agent_member") {
    return { name: comment.actor.displayName, image: null };
  }
  if (comment.user) {
    return {
      name: [comment.user.firstName, comment.user.lastName].filter(Boolean).join(" "),
      image: comment.user.profileImage,
    };
  }
  if (comment.actor) {
    return {
      name: comment.actor.displayName,
      image: comment.actor.kind === "human_user" ? comment.actor.profileImage : null,
    };
  }
  return { name: comment.authorName ?? "Unknown", image: null };
}
